import React, { useState, useEffect } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableItem } from "./SortableItem";

type ColumnProps = {
  column: {
    id: string;
    title: string;
    items: string[];
  };
  onSubmit?: () => void;
};

const Column: React.FC<ColumnProps> = ({ column, onSubmit }) => {
  const { setNodeRef } = useDroppable({ id: column.id });
  const baseGap = 10;
  const numItems = column.items.length;

  // Track window width for dynamic spacing.
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // For side columns, compute dynamic vertical gaps.
  let gaps: number[] = [];
  const totalGaps = numItems - 1;
  if (totalGaps === 1) {
    gaps = [baseGap * 30];
  } else if (totalGaps > 1) {
    const avgMultiplier = 30.7 / totalGaps - 0.6923;
    const m_min = avgMultiplier / 1.5;
    const m_max = 2 * m_min;
    for (let i = 0; i < totalGaps; i++) {
      const multiplierForGap = m_min + (m_max - m_min) * (i / (totalGaps - 1));
      gaps.push(baseGap * multiplierForGap);
    }
  }

  // Compute dynamic horizontal factor based on window width.
  const minWidth = 400;
  const maxWidth = 1600;
  const minFactor = 0.02;
  const maxFactor = 0.06;
  const clampedWidth = Math.max(minWidth, Math.min(windowWidth, maxWidth));
  const dynamicFactor =
    minFactor +
    ((clampedWidth - minWidth) / (maxWidth - minWidth)) *
      (maxFactor - minFactor);

  // --- Center Column ("in-progress") ---
  if (column.id === "in-progress") {
    const handleButtonClick = () => {
      setTimeout(() => {
        if (onSubmit) onSubmit();
      }, 300); // 300ms delay for visible transition
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Submission button rendered above the center column box */}
        <div
          style={{ textAlign: "center", marginBottom: "10px", width: "300px" }}
        >
          <button onClick={handleButtonClick} className="submit-button">
            Submit
          </button>
        </div>
        <div className="center-column">
          <h2
            style={{
              textAlign: "center",
              color: "#ffffff", // center column header text white
              fontFamily: "Roboto, sans-serif",
              fontWeight: 200,
              fontSize: "32px",
              margin: "5px 0",
            }}
          >
            Resonate the most with
          </h2>
          <div
            ref={setNodeRef}
            className="column-content"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: `${baseGap}px`,
              alignItems: "center",
            }}
          >
            {column.items.map((item) => (
              <SortableItem key={item} id={item} inCenter={true} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // --- Side Columns ("todo" and "done") ---
  return (
    <div className="column">
      {/* No header for side columns */}
      <div
        ref={setNodeRef}
        className="column-content"
        style={{ display: "flex", flexDirection: "column" }}
      >
        {column.items.map((item, index) => {
          // Generate random floating parameters for each card.
          const randomX1 = Math.random() * 40 - 20; // Range: -20 to 20px
          const randomY1 = Math.random() * 40 - 20;
          const randomX2 = Math.random() * 40 - 20;
          const randomY2 = Math.random() * 40 - 20;
          const randomDelay = Math.random() * 2; // 0 to 2s delay
          const randomDuration = Math.random() * 2 + 20; // Duration between 20 and 22 seconds
          return (
            <div
              key={item}
              style={{
                marginBottom:
                  index < totalGaps ? `${gaps[index]}px` : undefined,
                transform: "translateX(0px)",
                animationDelay: `${randomDelay}s`,
              }}
            >
              <SortableItem
                id={item}
                className="float"
                style={
                  {
                    "--float-x1": `${randomX1}px`,
                    "--float-y1": `${randomY1}px`,
                    "--float-x2": `${randomX2}px`,
                    "--float-y2": `${randomY2}px`,
                    animationDuration: `${randomDuration}s`,
                  } as React.CSSProperties
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Column;
